import React, { useEffect, useMemo, useRef, useState } from 'react' // Added useMemo
import mapboxgl from "mapbox-gl";
import { getBoundingBox, getCenter, getCenterLngLatLike, getMapCenterOffset, Location, mapboxToken, mapStyle } from '../../../../utils/map';
import { Box } from '@mui/system';
import { connectionStateColors } from '../../../../theme';
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { getStore, StoreState } from '../../../../state/configureStore';
import { closeAllMapPopups, closedMapPopup, openedMapPopup } from '../../../../state/actions/map';
import { MachineState } from '../../../../state/reducers/machine';
import './map_popup.css'
import { StepControl } from '../../../atoms/navigations/StepControl';
import { CustomerNode, StoreNode } from '../../../../state/reducers/customer';
import StoreMapPopup from './storeMapPopup';

type DashboardMapProps = {
  machines: MachineState[],
  stores: StoreNode[],
  sidebarOpen: boolean,
  sidebarWidth: number,
  sidebarOrientation: "horizontal" | "vertical"
}

type EnrichedStore = StoreNode & {
  cyclesCountToday: number;
  connectedCount: number;
  disconnectedCount: number;
  runningCount: number;
}

const storeSourceName = 'stores'

function centerOnLocations(map, locations, sidebarOpen, zoom) {
  if (map && map.current && locations?.filter(l => l !== undefined)?.length > 0) {
    const boundingBox = getBoundingBox(locations as Location[], 2)

    const sidebarOffset = getMapCenterOffset()

    map.current.fitBounds([
      [boundingBox[0].longitude, boundingBox[0].latitude],
      [boundingBox[1].longitude, boundingBox[1].latitude]
    ],
      {
        offset: sidebarOpen ? sidebarOffset : [0, 0]
      })
  }
}

function createStoreLayer(stores: EnrichedStore[]) {
  return {
    'type': 'FeatureCollection',
    'features': stores.map((s, index) => {
      return {
        'id': toFeatureId(s.id),
        'type': 'Feature',
        'geometry': {
          'type': 'Point',
          'coordinates': [
            s.location?.longitude, s.location?.latitude
          ],
        },
        'properties': {
          'iconSize': [40, 40],
          'serialId': s.id, 
          'name': s.name,
          'cyclesCountToday': s.cyclesCountToday,
          'connectedCount': s.connectedCount,
          'disconnectedCount': s.disconnectedCount,
          'runningCount': s.runningCount
        }
      }
    })
  };
}

function hashCode(str) {
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    let chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; 
  }
  return hash
}

function toFeatureId(id: string): number {
  return hashCode(id)
}

function setUpClusteredPopups(map) {
  map.on('click', 'clusters', (e) => {
    const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
    const clusterId = features[0].properties.cluster_id;

    map.getSource(storeSourceName).getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;

      map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom,
      });
    });
  });
}


const colors = [connectionStateColors.connected, connectionStateColors.disconnected];

function setupPopups(map, openedMapPopupAction, closedMapPopupAction, activePopupsRef: React.MutableRefObject<Record<string, mapboxgl.Popup>>) {
  const openPopup = (e: { features: { geometry: { coordinates: string | any[]; }; }[]; lngLat: { lng: number; }; }) => {
    map.getCanvas().style.cursor = 'pointer';

    const storeId = e.features[0]?.properties?.serialId;
    const openPopups = getStore().getState().map_state.openPopups 

    if (Object.keys(openPopups).includes(storeId)) return

    if (Object.keys(openPopups).length) {
      Object.keys(openPopups).forEach((id) => {
        closedMapPopupAction(id);
        
        const oldPopup = activePopupsRef.current[id];
        if (oldPopup) {
          oldPopup.remove();
        }
        
        delete activePopupsRef.current[id];
      });
    }

    const coordinates = e.features[0].geometry.coordinates.slice();
    const isDesktop = getStore().getState().dimensions.device === 'desktop'

    const popup = new mapboxgl.Popup({
      className: !isDesktop ? 'mapboxgl-popup-mobile' : 'mapboxgl-popup',
      closeButton: false,
      closeOnClick: true
    });

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    const popupDOM = document.createElement('div')
    popupDOM.setAttribute('id', 'popup_' + storeId) 

    popup.setLngLat(coordinates).setDOMContent(popupDOM).addTo(map);
    popup.on('close', () => {
      closedMapPopupAction(storeId)
    })

    activePopupsRef.current[storeId] = popup;

    openedMapPopupAction(storeId, [storeId])
  }

  map.on('click', 'store-layer', openPopup);

  map.on('mouseleave', 'store-layer', () => {
    map.getCanvas().style.cursor = '';
  });
}

const startPulseAnimation = (
  featureIds: number[],
  sourceName: string,
  mapInstance: any,
  duration: number
) => {
  let pulseAnimationId: number;
  let startTime: number | null = null;

  const animate = (timestamp: number) => {
    if (!startTime)
      startTime = timestamp;
    const elapsed = timestamp - startTime;
    const pulse = (Math.sin(elapsed / 300) + 1) / 2;

    featureIds.forEach((id) => {
      const state = mapInstance.getFeatureState({ source: sourceName, id });
      if (state?.animate) {
        mapInstance.setFeatureState({ source: sourceName, id }, { animate: true, pulse });
      }
    });

    pulseAnimationId = requestAnimationFrame(animate);
  };

  featureIds.forEach((id) => {
    mapInstance.setFeatureState({ source: sourceName, id }, { animate: true, pulse: 0 });
  });

  pulseAnimationId = requestAnimationFrame(animate);

  setTimeout(() => {
    featureIds.forEach((id) => {
      mapInstance.setFeatureState({ source: sourceName, id }, { animate: false, pulse: 0 });
    });
    cancelAnimationFrame(pulseAnimationId);
  }, duration);
};

const DashboardMap = (state: DashboardMapProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>) => {
  
  const enrichedStores: EnrichedStore[] = useMemo(() => {

    const storeDataMap = new Map<string, { cyclesCountToday: number, connectedCount: number, disconnectedCount: number, runningCount: number }>();

    for (const store of state.stores) {
      storeDataMap.set(store.id, { cyclesCountToday: 0, connectedCount: 0, disconnectedCount: 0, runningCount: 0 });
    }

    for (const machine of state.machines) {
      if (machine.nodeId && storeDataMap.has(machine.nodeId)) {
        const data = storeDataMap.get(machine.nodeId)!;
        data.cyclesCountToday += machine.cyclesCountToday || 0;
        
        if (machine.connectionState === 'Connected') {
          data.connectedCount += 1;
        } else {
          data.disconnectedCount += 1;
        }

        if (machine.runningState === 'running') {
          data.runningCount += 1;
        }
      }
    }

    return state.stores
      .filter(store => store.location) 
      .map(store => {
        const aggData = storeDataMap.get(store.id) || { cyclesCountToday: 0, connectedCount: 0, disconnectedCount: 0, runningCount: 0 };
        return {
          ...store,
          ...aggData,
        };
      });
  }, [state.stores, state.machines]);

  const locations = enrichedStores?.map(s => s.location)
  const center: Location = locations?.length ? getCenter(locations) : { latitude: 57.72101, longitude: 12.9401 }

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const activePopupsRef = useRef<Record<string, mapboxgl.Popup>>({});
  
  const zoom = locations?.length ? 9 : 7;

  useEffect(() => {
    if (Object.keys(state.openPopups).length) {

    }
    else {
      centerOnLocations(map, locations, state.sidebarOpen, zoom)
    }
  }, [state.dimensions.orientation])

  useEffect(() => {
    if (map?.current && map.current?.getSource(storeSourceName)) {
      
      enrichedStores?.forEach((s, index) => {
        map.current.setFeatureState({
          source: storeSourceName,
          id: toFeatureId(s.id)
        }, {
          isRunning: s.runningCount > 0,
          isConnected: s.connectedCount > 0
        });
      })

      const updatedData = createStoreLayer(enrichedStores)
      map.current?.getSource(storeSourceName).setData(updatedData)
    }
  }, [enrichedStores]) 
  

  const previousCyclesCount = useRef<Record<string, number>>({});


  useEffect(() => {
    if (!map.current) return;

    const storesToAnimate: number[] = [];
    const updatedCycles: Record<string, number> = {};
    
    enrichedStores.forEach((store) => { 
      const storeId = store.id; 
      const featureId = toFeatureId(store.id);
      const currentValue = store.cyclesCountToday; 
      const prevValue = previousCyclesCount.current[storeId]; 

      updatedCycles[storeId] = currentValue; 

      if (prevValue !== undefined && currentValue !== prevValue) {
        storesToAnimate.push(featureId);
      }
    });

    if (storesToAnimate.length > 0) {
      startPulseAnimation(storesToAnimate, storeSourceName, map.current, 24000);
    }

    previousCyclesCount.current = updatedCycles;
  }, [enrichedStores]);


  const previousClusterSums = useRef<Record<number, number>>({});

  useEffect(() => {
    if (!map.current) return;

    let animationFrame: number;

    const checkClusters = () => {
      if (!map.current || !map.current.getSource(storeSourceName)) {
        animationFrame = requestAnimationFrame(checkClusters);
        return;
      }
        
      const features = map.current.querySourceFeatures(storeSourceName, {
        filter: ['has', 'point_count']
      });

      const clustersToAnimate: number[] = [];

      features.forEach((feature) => {
        const clusterId = feature.id;
        if (clusterId == null) return;

        const currentSum = feature.properties?.cyclesTodaySum;
        const prevSum = previousClusterSums.current[clusterId];

        if (prevSum !== undefined && currentSum !== prevSum) {
          clustersToAnimate.push(clusterId);
        }

        previousClusterSums.current[clusterId] = currentSum;
      });

      if (clustersToAnimate.length > 0) {
        startPulseAnimation(clustersToAnimate, storeSourceName, map.current, 24000);
      }

      animationFrame = requestAnimationFrame(checkClusters);
    };

    animationFrame = requestAnimationFrame(checkClusters);

    return () => cancelAnimationFrame(animationFrame);
  }, [state.machines]); 

  useEffect(() => {
    if (map.current) return;

    mapboxgl.accessToken = mapboxToken;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/seerefine/' + mapStyle,
      center: [center.longitude, center.latitude],
      zoom: zoom
    });
    centerOnLocations(map, locations, state.sidebarOpen, zoom)

    const geojson = createStoreLayer(enrichedStores)

    map.current.on('load', () => {

      map.current.addSource(storeSourceName, {
        'type': 'geojson',
        'data': geojson, 
        "cluster": true,
        "clusterMaxZoom": 18,
        "clusterRadius": 35,
        "clusterProperties": {
          "cyclesTodaySum": ['+', ['get', 'cyclesCountToday']],
          "connected": ['+', ['get', 'connectedCount']],
          "disconnected": ['+', ['get', 'disconnectedCount']],
        }
      });

      map.current.addLayer(
        {
          'id': 'store-layer', 
          'type': 'circle',
          'source': storeSourceName,
          "filter": ['!', ['has', 'point_count']],
          'paint': {
            'circle-radius': [
              'case',
              ['>=', ['get', 'cyclesCountToday'], 100], 20,
              ['>=', ['get', 'cyclesCountToday'], 10], 16,
              12
            ],
            'circle-color': connectionStateColors.connected,
          }
        }
      );

      map.current.addLayer(
        {
          'id': 'store-cycles-layer', 
          'type': 'symbol',
          'source': storeSourceName,
          "filter": ['!', ['has', 'point_count']],
          'layout': {
            'text-field': ['get', 'cyclesCountToday'],
            'text-size': 12,
          },
          paint: {
            'text-color': '#fff'
          }
        })

      map.current.addLayer({
        id: 'cluster-background',
        type: 'circle',
        source: storeSourceName,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color':
            connectionStateColors.connected,
          'circle-radius': [
            'case',
            ['>=', ['get', 'point_count'], 100], 32,
            ['>=', ['get', 'point_count'], 50], 28, 
            ['>=', ['get', 'point_count'], 10], 24,  
            18
          ],
        }
      });

      map.current.addLayer({
        id: 'cluster-pulse',
        type: 'circle',
        source: storeSourceName,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#ffffff',
          'circle-opacity': [
            'case',
            ['boolean', ['feature-state', 'animate'], false],
            ['interpolate', ['linear'], ['feature-state', 'pulse'], 0, 0.1, 1, 0.6],
            0
          ],
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'animate'], false],
            ['interpolate', ['linear'], ['feature-state', 'pulse'], 0, 26, 1, 36],
            0
          ]
        }
      }, 'cluster-background');

      map.current.addLayer({
        id: 'store-pulse',
        type: 'circle',
        source: storeSourceName,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#ffffff',
          'circle-opacity': [
            'case',
            ['boolean', ['feature-state', 'animate'], false],
            ['interpolate', ['linear'], ['feature-state', 'pulse'], 0, 0.1, 1, 0.6],
            0
          ],
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'animate'], false],
            ['interpolate', ['linear'], ['feature-state', 'pulse'], 0, 18, 1, 30],
            0
          ]
        }
      }, 'store-layer'); 

      map.current.addLayer({
        "id": 'clusters',
        "type": 'circle',
        "source": storeSourceName,
        "filter": ['has', 'point_count'],
        paint: {
          'circle-color': "#fff",
          'circle-radius': [
            'case',
            ['>=', ['get', 'point_count'], 100], 25, // 100+ stores
            ['>=', ['get', 'point_count'], 50], 18,  // 50+ stores
            ['>=', ['get', 'point_count'], 10], 14,  // 10+ stores
            10
          ]
        }
      });

      map.current.addLayer({
        "id": 'cluster-count',
        "type": 'symbol',
        "source": storeSourceName,
        "filter": ['has', 'point_count'],
        "layout": {
          'text-field': ['get', 'cyclesTodaySum'], 
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': [ 
            'case',
            ['>=', ['get', 'point_count'], 100], 22, // 100+ stores
            ['>=', ['get', 'point_count'], 50], 20,  // 50+ stores
            ['>=', ['get', 'point_count'], 10], 18,  // 10+ stores
            16
          ],
        },
        "paint": {
          "text-color": "#000",
        }
      });

      map.current.on('mouseenter', 'clusters', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });

      // Change it back to a pointer when it leaves
      map.current.on('mouseleave', 'clusters', () => {
        map.current.getCanvas().style.cursor = '';
      });

      map.current.on('zoom', () => {
        if (!map.current || !map.current.getSource(storeSourceName)) return;
        const features = map.current.querySourceFeatures(storeSourceName, {
          filter: ['has', 'point_count']
        });
        // clear previous cluster sums so we dont animate old data id zoom changes
        previousClusterSums.current = {}
        features.forEach((feature) => {

          const clusterId = feature.id;
          if (clusterId == null)
            return;

          const currentSum = feature.properties?.cyclesTodaySum;

          previousClusterSums.current[clusterId] = currentSum;
        });
      })

    });

    setUpClusteredPopups(map.current)
    setupPopups(map.current, state.openedMapPopup, state.closedMapPopup, activePopupsRef)

  })

  return (
    <Box style={{
      height: '100%',
      width: '100%',
    }}>
      <Box style={{
        height: '100%',
        width: '100%',
      }} ref={mapContainer} className="map-container" />
      
      {Object.keys(state?.openPopups || {}).length > 0 && (
        <>
          {Object.keys(state.openPopups)
            .map(popupId => {
              const openPopup = activePopupsRef.current[popupId];
              const domEl = document.getElementById('popup_' + popupId);

              if (!domEl) return null;

              return ReactDOM.createPortal(
                <PopupSlideComponent
                  mapPopup={openPopup}
                  storeId={popupId}
                  stores={enrichedStores}
                />,
                domEl
              );
            })
            .filter(Boolean)}
        </>
      )}
    </Box>
  );
}

type PopupSlideProps = {
  storeId: string, 
  mapPopup: any,
  stores: EnrichedStore[]
}

export type FoundStoreProps = {
  store: EnrichedStore
}

const PopupSlideComponent = ({ storeId, mapPopup, stores }: PopupSlideProps) => {

  const store = useMemo(() => {
 
    if (!storeId) {
      return undefined;
    }
    
    return stores.find(store => store.id === storeId);
   

  }, [storeId, stores]);

  if (!storeId) {
    return <Box sx={{ p: 2, minWidth: '200px', color: '#fff' }}>No store at this location.</Box>
  }

  if (!store) {

    return <Box sx={{ p: 2, minWidth: '200px', color: '#fff' }}>Loading store data...</Box>
  }

  return <Box style={{
    height: '100%',
    width: '100%'
  }}>
    <StoreMapPopup store={store} />
  </Box>
}

const mapStateToProps = (state: StoreState) => ({
  openPopups: state.map_state.openPopups,
  dimensions: state.dimensions,
  stores: state.customers.stores.filter(s => s.location && !s.hidden),
  machines: state.machines
})

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  closeAllMapPopups: () => dispatch(closeAllMapPopups()),
  closedMapPopup: (id: string) => dispatch(closedMapPopup(id)),
  openedMapPopup: (id: string, dataIds: string[]) => dispatch(openedMapPopup(id, dataIds)),
})

export default connect(mapStateToProps, mapDispatchToProps)(DashboardMap)